import DOMPurify from 'dompurify';
import { CheckboxOnlyAfterClickLinkWidget } from 'src/components/datausageagreement/widgets/CheckboxOnlyAfterClickLinkWidget';
import { ConditionallyDisabledInput } from 'src/components/datausageagreement/widgets/ConditionallyDisabledInput';
import { ReadOnlyTextWidget } from 'src/components/datausageagreement/widgets/ReadOnlyTextWidget';
import { GroupedMultiselectWidget } from 'src/components/datausageagreement/widgets/GroupedMultiselectWidget';
import {
  HierarchicalCheckboxField,
  flattenTreeToText,
} from 'src/components/datausageagreement/widgets/HierarchicalCheckboxField';
import * as React from 'react';
import { useMemo } from 'react';
import { FieldProps, RJSFSchema, WidgetProps } from '@rjsf/utils';

export const DEFAULT_WIDGETS = {
  CheckboxWidget: (props: WidgetProps) => <CheckboxOnlyAfterClickLinkWidget {...props} />,
  TextWidget: (props: WidgetProps) => <ConditionallyDisabledInput {...props} />,
  GroupedMultiselectWidget: (props: WidgetProps) => <GroupedMultiselectWidget {...props} />,
};

export const TEXT_ONLY_WIDGETS = {
  CheckboxWidget: (props: WidgetProps) => <CheckboxOnlyAfterClickLinkWidget {...props} />,
  TextWidget: (props: WidgetProps) => <ReadOnlyTextWidget {...props} />,
  SelectWidget: (props: WidgetProps) => <ReadOnlyTextWidget {...props} />,
  RadioWidget: (props: WidgetProps) => <ReadOnlyTextWidget {...props} />,
  GroupedMultiselectWidget: (props: WidgetProps) => <ReadOnlyTextWidget {...props} />,
};

export const DEFAULT_FIELDS = {
  HierarchicalCheckboxField: (props: FieldProps) => <HierarchicalCheckboxField {...props} />,
};

export const TEXT_ONLY_FIELDS = {
  HierarchicalCheckboxField: (props: FieldProps) => {
    const nodes = Array.isArray(props.formData) ? props.formData : [];
    return <>{flattenTreeToText(nodes).join(', ')}</>;
  },
};

export const FORM_CONTEXTS_FOR_REQUESTS = ['default', 'requests'];

export const FORM_CONTEXTS_FOR_REVIEWS = ['reviews'];

export const sanitizeSchema = (schema: RJSFSchema): RJSFSchema => {
  const sanitizeValue = (value: any): any => {
    if (typeof value === 'string') {
      return DOMPurify.sanitize(value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    }
    if (Array.isArray(value)) {
      return value.map(sanitizeValue);
    }
    if (value && typeof value === 'object') {
      const sanitized: any = {};
      for (const [key, val] of Object.entries(value)) {
        sanitized[key] = sanitizeValue(val);
      }
      return sanitized;
    }
    return value;
  };
  return sanitizeValue(schema);
};

export const sanitizeFormData = (data: any): any => {
  if (typeof data === 'string') {
    return DOMPurify.sanitize(data, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  }
  if (Array.isArray(data)) {
    return data.map(sanitizeFormData);
  }
  if (data && typeof data === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      sanitized[key] = sanitizeFormData(value);
    }
    return sanitized;
  }
  return data;
};

export const validateSchemaStructure = (schema: any): boolean => {
  if (!schema || typeof schema !== 'object') return false;
  if (schema.type && !['object', 'string', 'number', 'boolean', 'array'].includes(schema.type)) return false;
  return true;
};

/** Default context applied to fields without explicit showInContexts */
const DEFAULT_CONTEXT = 'default';

/**
 * Checks if a field should be visible based on the active contexts.
 * Fields without showInContexts default to showing in 'default' context only.
 */
const isFieldVisible = (field: any, contexts: string[]): boolean => {
  const fieldContexts = Array.isArray(field?.showInContexts) ? field.showInContexts : [DEFAULT_CONTEXT];
  return fieldContexts.some((ctx) => contexts.includes(ctx));
};

/**
 * Applies context-specific overrides to field properties (title, description).
 * Removes context metadata from the output schema.
 */
const applyContextOverrides = (field: any, contexts: string[]): any => {
  if (!field?.contextOverrides || typeof field.contextOverrides !== 'object') return field;

  const result = { ...field };
  // Find first matching non-default context
  const matchingContext = contexts.find((ctx) => ctx !== DEFAULT_CONTEXT && field.contextOverrides[ctx]);

  if (matchingContext) {
    const overrides = field.contextOverrides[matchingContext];
    if (overrides.title !== undefined) result.title = overrides.title;
    if (overrides.description !== undefined) result.description = overrides.description;
  }

  // Clean up context metadata from output schema
  delete result.contextOverrides;
  delete result.showInContexts;
  return result;
};

/**
 * Filters a JSON schema to only include fields visible in the given contexts.
 *
 * Each field can declare:
 * - showInContexts: string[] — which contexts it appears in (defaults to ['default'])
 * - contextOverrides: { [context]: { title?, description? } } — per-context label overrides
 *
 * Also recurses into allOf/anyOf/oneOf and then/else so that conditional fields
 * (defined via allOf[if/then]) are filtered by context too. The `if` blocks are
 * left untouched to preserve RJSF's condition evaluation logic.
 *
 * @param schema - The RJSF schema to filter
 * @param contexts - Active contexts, e.g. ['default', 'reviews']
 * @returns A new schema containing only fields visible in the given contexts
 */
export const filterSchemaByContexts = (schema: RJSFSchema, contexts: string[]): RJSFSchema => {
  if (!schema || typeof schema !== 'object') return schema;
  if (!contexts?.length) contexts = [DEFAULT_CONTEXT];

  const filterProperties = (properties: Record<string, any>): Record<string, any> | null => {
    const filtered: Record<string, any> = {};

    for (const [key, field] of Object.entries(properties)) {
      if (!isFieldVisible(field, contexts)) continue;

      let processedField = applyContextOverrides(field, contexts);

      // Recurse into nested objects
      if (processedField.type === 'object' && processedField.properties) {
        const filteredProps = filterProperties(processedField.properties);
        if (!filteredProps || Object.keys(filteredProps).length === 0) continue; // Hide empty parents
        processedField = {
          ...processedField,
          properties: filteredProps,
          required: processedField.required?.filter((r: string) => r in filteredProps),
        };
      }

      // Recurse into array items
      if (processedField.type === 'array' && processedField.items?.properties) {
        const filteredItems = filterSchemaByContexts(processedField.items, contexts);
        if (!filteredItems.properties || Object.keys(filteredItems.properties).length === 0) continue;
        processedField = { ...processedField, items: filteredItems };
      }

      filtered[key] = processedField;
    }

    return Object.keys(filtered).length > 0 ? filtered : null;
  };

  const result: RJSFSchema = { ...schema };

  if (result.properties) {
    const filteredProps = filterProperties(result.properties);
    result.properties = filteredProps || {};

    // Keep required/order arrays in sync with visible properties
    if (result.required) {
      result.required = result.required.filter((r: string) => r in result.properties);
    }
    if (result['order']) {
      result['order'] = result['order'].filter((o: string) => o in result.properties);
    }
  }

  // Also filter inside allOf/anyOf/oneOf and then/else so conditional fields
  // (e.g. s3_info inside allOf[then]) respect showInContexts too.
  // We skip `if` blocks intentionally — filtering them would break condition evaluation.
  for (const key of ['allOf', 'anyOf', 'oneOf'] as const) {
    if (Array.isArray(result[key])) {
      result[key] = (result[key] as RJSFSchema[]).map((subSchema) => filterSchemaByContexts(subSchema, contexts));
    }
  }

  for (const key of ['then', 'else'] as const) {
    if (result[key]) {
      result[key] = filterSchemaByContexts(result[key] as RJSFSchema, contexts);
    }
  }

  return result;
};

export const useRjsfSchema = (
  schema: string,
  setPropertyOrder: (order: any) => void,
  setUiSchemaFromJson?: (uiSchema: any) => void,
) => {
  return useMemo(() => {
    try {
      const rawSchema = JSON.parse(schema) as object;
      const parsedSchema = rawSchema as RJSFSchema;
      if (!!rawSchema?.['order']) {
        setPropertyOrder(rawSchema['order']);
      }
      if (!!rawSchema?.['uiSchema'] && setUiSchemaFromJson) {
        setUiSchemaFromJson(rawSchema['uiSchema']);
      }
      if (!validateSchemaStructure(parsedSchema)) {
        throw new Error('Invalid schema structure');
      }
      return sanitizeSchema(parsedSchema);
    } catch (error) {
      console.error('Invalid schema provided:', error);
      return { type: 'object', properties: {} } as RJSFSchema;
    }
  }, [schema]);
};
